import { Button, Card, CardBody, Flex, Icon, Stack, Text } from "@chakra-ui/react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { FiArrowRight, FiEdit3, FiEye } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  PortalLifecycleStatus,
  BidStatus,
  useCreateBidMutation,
  useGetPortalMyBidsQuery,
  useGetPortalRegistrationStatusQuery,
  type PortalNegotiationRoundDto,
  type PortalSourcingEventDetailDto,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { useDisplayDateWithTime } from "../../../../common/formatting/useDisplayDateWithTime";
import { useAppSelector } from "../../../../common/redux/hooks";
import { isDpsCompetition } from "../../buyer/wizard/utils/procedure";
import { ownPortalBid } from "../ownPortalBid";
import { settledOwnBid } from "../settledOwnBid";
import { usePortalEventUrls } from "../usePortalEventUrls";
import { isPrivatePortalEvent } from "../view-single/PrivateInviteCta";
import { type RoundExit } from "../view-single/missedPortalRound";
import { useOpenPortalRound } from "../view-single/useOpenPortalRound";
import { isReadonlyBidStatus } from "./readonly/readonlyBidStatus";

export const StartBidCta = ({ event }: { event: PortalSourcingEventDetailDto }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const portalUrls = usePortalEventUrls();
  const displayer = useApiError();
  const displayDateWithTime = useDisplayDateWithTime();
  const authState = useAppSelector((state) => state.authState.state);
  const [createBid, { isLoading }] = useCreateBidMutation();

  const isLoggedInWithOrg = authState?.type === "LoggedInWithOrgContext";
  const isOpen = event.lifecycle.status === PortalLifecycleStatus.Open;
  const privateEvent = isPrivatePortalEvent(event);

  // The purchase is served only to the suppliers it invited (§ 26-7), so seeing it is the ticket.
  const invitedByScheme = isDpsCompetition(event.procedureType);

  const { data: myBids } = useGetPortalMyBidsQuery({}, { skip: !isLoggedInWithOrg });
  const ownBid = ownPortalBid(myBids?.bids, event.id);
  const {
    round: openRound,
    exit,
    isLoading: roundsLoading,
    isError: roundsFailed,
  } = useOpenPortalRound(event, ownBid?.awardOutcome);

  // Registering interest is the entry point; the offer CTA only appears once the supplier is following the procurement.
  const { data: status } = useGetPortalRegistrationStatusQuery(
    { eventId: event.id },
    { skip: !isLoggedInWithOrg || !isOpen || !!event.competitionStage || privateEvent || !!ownBid }
  );

  if (!isLoggedInWithOrg) return null;

  const openBid = (bidId: string) =>
    navigate(
      privateEvent ? portalUrls.privateBid.edit.go(event.id, bidId) : portalUrls.bid.edit.go(event.id, bidId).intro,
    );

  if (ownBid) {
    if (isOpen && (event.competitionStage || privateEvent)) return null;
    if (ownBid.status !== BidStatus.Rejected) {
      if (roundsLoading) return null;
      const deliveredOrCurrentBidId = ownBid.deliveredBidId ?? ownBid.bidId;
      if (roundsFailed) {
        const cta = event.designContest ? unknownStatusEntryCta(t) : unknownRoundsCta(t);
        return <BidCard cta={cta} onClick={() => openBid(deliveredOrCurrentBidId)} />;
      }
      if (exit && !isOpen) {
        return <BidCard cta={roundExitCta(exit, t)} onClick={() => openBid(deliveredOrCurrentBidId)} />;
      }
    }
    const shown = settledOwnBid(ownBid, isOpen || openRound != null);
    const cta =
      !isOpen && openRound && shown.status === BidStatus.Draft
        ? roundDraftCta(openRound, displayDateWithTime(openRound.deadline), t)
        : deriveCta(shown.status, isOpen, t, !!event.designContest);
    return <BidCard cta={cta} onClick={() => openBid(shown.bidId)} />;
  }

  // Two-stage competitions gate the offer on the invitation (ParticipationCta), not registration;
  // private procurements start bids from PrivateBidCta.
  if (!isOpen || event.competitionStage || privateEvent) return null;
  if (!status || (!invitedByScheme && !status.hasRegistered)) return null;

  const startNewBid = async () => {
    try {
      const bid = await createBid({ eventId: event.id }).unwrap();
      navigate(portalUrls.bid.edit.go(event.id, bid.id).intro);
    } catch (e) {
      displayer.trigger(e);
    }
  };

  return <BidCard cta={deriveCta(null, isOpen, t, !!event.designContest)} onClick={startNewBid} isLoading={isLoading} />;
};

const BidCard = ({
  cta,
  onClick,
  isLoading = false,
}: {
  cta: ReturnType<typeof deriveCta>;
  onClick: () => void;
  isLoading?: boolean;
}) => (
  <Card variant="filled" bg="smPrimarySofter" rounded="xl">
    <CardBody>
      <Flex direction={{ base: "column", md: "row" }} align={{ md: "center" }} justify="space-between" gap="4">
        <Stack spacing="1">
          <Text fontWeight="semibold">{cta.title}</Text>
          <Text fontSize="sm" color="smMuted">
            {cta.subtitle}
          </Text>
        </Stack>
        <Button colorScheme="teal" leftIcon={<Icon as={cta.icon} />} onClick={onClick} isLoading={isLoading} flexShrink={0}>
          {cta.button}
        </Button>
      </Flex>
    </CardBody>
  </Card>
);

const roundExitCta = (exit: RoundExit, t: TFunction) => ({
  title:
    exit.reason === "declined"
      ? t("Your offer")
      : t("You are not part of the next round unless the buyer invites you again"),
  subtitle:
    exit.reason === "declined"
      ? t("You declined negotiation round {{round}}. You can still view the offer you delivered.", {
          round: exit.round.roundNumber,
        })
      : t("You did not answer negotiation round {{round}} by its deadline. You can still view the offer you delivered.", {
          round: exit.round.roundNumber,
        }),
  button: t("View offer"),
  icon: FiEye,
});

const roundDraftCta = (round: PortalNegotiationRoundDto, deadline: string, t: TFunction) => ({
  title: t("You have started a new offer in negotiation round {{round}}", { round: round.roundNumber }),
  subtitle: t("It is a draft. You can continue it until the round's deadline, {{deadline}}.", { deadline }),
  button: t("Continue new bid"),
  icon: FiArrowRight,
});

const unknownRoundsCta = (t: TFunction) => ({
  title: t("Your offer"),
  subtitle: t("We could not check the negotiation rounds right now. You can still open your offer."),
  button: t("View offer"),
  icon: FiEye,
});

const unknownStatusEntryCta = (t: TFunction) => ({
  title: t("Your entry"),
  subtitle: t("We could not check the status right now. You can still open your entry."),
  button: t("View entry"),
  icon: FiEye,
});

const deriveCta = (
  ownBidStatus: BidStatus | null,
  isOpen: boolean,
  t: (key: string) => string,
  designContest = false,
) => {
  if (ownBidStatus == null && designContest) {
    return {
      title: t("Want to take part in the design contest?"),
      subtitle: t("Build your entry step by step. We save your progress automatically."),
      button: t("Start entry"),
      icon: FiEdit3,
    };
  }
  if (ownBidStatus == null) {
    return {
      title: t("Want to compete for this contract?"),
      subtitle: t("Build your offer step by step. We save your progress automatically."),
      button: t("Start offer"),
      icon: FiEdit3,
    };
  }
  if (ownBidStatus === BidStatus.Rejected) {
    return {
      title: designContest ? t("Your entry was rejected") : t("Your offer was rejected"),
      subtitle: designContest
        ? t("Open the entry to read why it was rejected and what you can do.")
        : t("Open the offer to read why it was rejected and what you can do."),
      button: designContest ? t("View entry") : t("View offer"),
      icon: FiEye,
    };
  }
  if (isReadonlyBidStatus(ownBidStatus)) {
    return {
      title: designContest ? t("Your entry has been delivered") : t("Your offer has been delivered"),
      subtitle: designContest ? t("You can view the entry you submitted.") : t("You can view the offer you submitted."),
      button: designContest ? t("View entry") : t("View offer"),
      icon: FiEye,
    };
  }
  if (!isOpen) {
    return {
      title: designContest ? t("Your entry was not delivered") : t("Your offer was not delivered"),
      subtitle: t("The deadline has passed. You can still open and read it."),
      button: designContest ? t("View entry") : t("View offer"),
      icon: FiEye,
    };
  }
  return {
    title: designContest ? t("You have a saved entry") : t("You have a saved offer"),
    subtitle: t("Continue where you left off — we save your progress automatically."),
    button: designContest ? t("Continue entry") : t("Continue offer"),
    icon: FiArrowRight,
  };
};
